import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@rekrytering/ui/components/dropdown-menu'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { BellIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type NotificationType = Doc<'notifications'>['type']

const formatRelativeTime = (createdAt: number): string => {
  const diffMs = Date.now() - createdAt
  const diffMinutes = Math.floor(diffMs / 60000)
  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return `${Math.floor(diffHours / 24)}d ago`
}

const NOTIFICATION_TYPE_KEYS = {
  interview_completed: 'notifications.typeInterviewCompleted',
  interview_failed: 'notifications.typeInterviewFailed',
  low_balance: 'notifications.typeLowBalance',
  job_activated: 'notifications.typeJobActivated',
  candidate_expired: 'notifications.typeCandidateExpired',
} as const satisfies Record<NotificationType, string>

export const NotificationBell = () => {
  const { t } = useTranslation()

  const { data: notifications = [] } = useQuery(
    convexQuery(
      api.features.notifications.notifications.listMyNotifications,
      {},
    ),
  )
  const { data: unreadCount = 0 } = useQuery(
    convexQuery(
      api.features.notifications.notifications.getMyUnreadNotificationCount,
      {},
    ),
  )

  const { mutateAsync: markAsRead } = useMutation({
    mutationFn: useConvexMutation(
      api.features.notifications.notifications.markMyNotificationAsRead,
    ),
  })
  const { mutateAsync: markAllAsRead } = useMutation({
    mutationFn: useConvexMutation(
      api.features.notifications.notifications.markAllMyNotificationsAsRead,
    ),
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button variant="ghost" size="icon" className="relative">
          <BellIcon />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0 text-sm font-medium">
            {t('notifications.title')}
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button variant="ghost" size="xs" onClick={() => markAllAsRead({})}>
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <p className="px-2 py-4 text-center text-sm text-primary/50">
            {t('notifications.empty')}
          </p>
        ) : (
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification._id}
              className={cn(
                'flex flex-col items-start gap-0.5 px-3 py-2',
                !notification.readAt && 'bg-muted/40',
              )}
              onClick={() => {
                if (!notification.readAt) {
                  markAsRead({ notificationId: notification._id })
                }
              }}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <span className="text-xs font-medium text-primary/50">
                  {t(NOTIFICATION_TYPE_KEYS[notification.type])}
                </span>
                <span className="text-xs text-primary/40">
                  {formatRelativeTime(notification.createdAt)}
                </span>
              </div>
              <span
                className={cn(
                  'text-sm',
                  notification.readAt
                    ? 'text-primary/60'
                    : 'font-medium text-primary',
                )}
              >
                {notification.title}
              </span>
              <span className="text-xs text-primary/50">
                {notification.body}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
